type TranslateFn = (key: string, params?: Record<string, unknown>) => string

export const localizedField = (obj: { ru: string; ky: string }, locale: string): string =>
  obj[locale as 'ru' | 'ky'] || obj.ru

export const toCurrencyCode = (currency: string): string => currency.toLowerCase()

const organizationTypeLabelKeys: Record<string, string> = {
  bank: 'common.bank',
  microcredit_company: 'common.microcredit_company'
}

export const organizationTypeLabel = (type: string, t: TranslateFn): string => {
  const key = organizationTypeLabelKeys[type]
  return key ? t(key) : type
}

// Russian plural form (one / few / many) for a count.
const pluralForm = (n: number): 'one' | 'few' | 'many' => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'one'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few'
  return 'many'
}

export const formatPeriodMonths = (months: number | null, t: TranslateFn): string | null => {
  if (!months) return null
  const isYears = months % 12 === 0
  const n = isYears ? months / 12 : months
  const unit = isYears ? 'year' : 'month'
  return t(`widget_ai_chat.period.${unit}_${pluralForm(n)}`, { n })
}

// Russian genitive plural after "до" only distinguishes one/many (no separate
// "few" form): "до 1 года" vs "до 2 лет" / "до 5 лет" both use the "many" word.
const genitiveForm = (n: number): 'one' | 'many' => (pluralForm(n) === 'one' ? 'one' : 'many')

// "до N лет/месяцев" — always genitive after "до" in Russian, regardless of n
// (unlike widget_ai_chat.period, used without a preposition in
// insurance-product, which needs the full one/few/many nominative split).
export const formatUpToPeriodMonths = (months: number | null, t: TranslateFn): string | null => {
  if (!months) return null
  if (months < 12) {
    return months === 1
      ? t('widget_ai_chat.up_to_period.month_one', { months })
      : t('widget_ai_chat.up_to_period.month_many', { months })
  }

  const years = Math.floor(months / 12)
  const remainderMonths = months % 12
  const yearsPart = t(`widget_ai_chat.up_to_period.year_word_${genitiveForm(years)}`, { n: years })
  if (remainderMonths === 0) return t('widget_ai_chat.up_to_period.year', { period: yearsPart })

  const monthsPart = t(`widget_ai_chat.up_to_period.month_word_${genitiveForm(remainderMonths)}`, {
    n: remainderMonths
  })
  return t('widget_ai_chat.up_to_period.year_month', {
    period: `${yearsPart} ${monthsPart}`
  })
}

const rateFmt = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

export const formatRate = (rate: number): string => `${rateFmt.format(rate)}%`

const amountFmt = new Intl.NumberFormat('ru-RU')

export const formatAmountRange = (
  from: number | null,
  to: number | null,
  t: TranslateFn
): string | null => {
  if (from && to) return `${amountFmt.format(from)} – ${amountFmt.format(to)}`
  if (from) return `${t('common.from_preposition')} ${amountFmt.format(from)}`
  if (to) return `${t('common.up_to')} ${amountFmt.format(to)}`
  return null
}
